import { useState, useContext } from 'react'
import { Wrapper } from 'styles/styleSystem/wrapper'
import { SearchPageListHeaderWrapper } from '../styles'
import { Tooltip } from 'antd'
import { MdClear, MdSettings } from 'react-icons/md'
import { SearchContext } from 'contexts/SearchContext'
import _isEmpty from 'lodash-es/isEmpty'
import _isNumber from 'lodash-es/isNumber'
import _omit from 'lodash-es/omit'
import { Divider } from 'components/Divider'
import { useWindowSize } from 'contexts/WindowSizeContext'
import { SearchResults } from 'components/SearchResults'
import { LoggedInAction } from 'components/LoggedInAction'
import { StringParam, useQueryParam } from 'next-query-params'
import _isEqual from 'lodash-es/isEqual'
import _some from 'lodash-es/some'
import dynamic from 'next/dynamic'

const SearchAlerts = dynamic(() =>
  import('./SearchAlerts').then((res) => res.SearchAlerts)
)
const SaveSearch = dynamic(() =>
  import('./SaveSearch').then((res) => res.SaveSearch)
)

const SearchSort = dynamic(() =>
  import('components/SearchSort').then((res) => res.SearchSort)
)
const JobSearchSettings = dynamic(() =>
  import('components/Modals/SearchSettingsModal/JobSearchSettings').then(
    (res) => res.JobSearchSettings
  )
)

export const JobSearchPageListHeader: React.FC<{
  hideBottomBorder: boolean
  listLength: number
}> = ({ hideBottomBorder, listLength }) => {
  const [settingsOpen, setSettingsOpen] = useState(false)
  const { isMobile } = useWindowSize()
  const [editing, setEditing] = useState(false)
  const {
    overview,
    searchState,
    clearSearchState,
    updateSearchProperty,
    filters,
    loading,
  } = useContext(SearchContext)
  const [term, setTerm] = useQueryParam('term', StringParam)

  const sortOptions = [
    'relevancy',
    ...Object.keys(filters).map((key) =>
      key === 'location_permutations'
        ? 'location'
        : key === 'is_accepts_anonymous_application'
        ? 'anonymous_apply'
        : key
    ),
  ]

  const onClearTerm = () => {
    setTerm(undefined)
  }

  const getAllFilters = () => {
    const filters = []
    Object.keys(searchState).forEach((filter) => {
      if (!_isEmpty(searchState[filter]) || _isNumber(searchState[filter])) {
        filters.push(filter)
      }
    })
    return filters
  }

  const allFiltersCount = getAllFilters().length - 2
  const hideClearButton =
    allFiltersCount === 1 && getAllFilters().includes('searchText')

  const clearSearch = () => {
    clearSearchState()
  }

  const sortSetter = (val: string) => {
    let targetField = val
    if (val === 'location') {
      targetField = 'location_permutations'
    }

    if (val === 'anonymous_apply') {
      targetField = 'is_accepts_anonymous_application'
    }
    updateSearchProperty('sort', targetField)
  }

  return (
    <SearchPageListHeaderWrapper hideBottomBorder={hideBottomBorder}>
      <SearchResults
        count={listLength}
        totalCount={overview?.total_results}
        term={searchState.searchText}
        clearTerm={onClearTerm}
        loading={loading}
      />
      {!isMobile && (
        <Wrapper display="flex" alignItems="center">
          <SearchSort
            sortOptions={sortOptions}
            currentSort={searchState.sort}
            onSetSortDirection={(direction) =>
              updateSearchProperty('sortDirection', direction)
            }
            onSort={sortSetter}
            currentDirection={searchState.sortDirection}
          />
          <Divider vertical height={20} margin={isMobile ? 5 : 10} />
          {allFiltersCount > 0 && (
            <>
              {/* Alerts */}
              <SearchAlerts searchState={searchState} editingAlert={editing} />
              {/* ------- */}
              <Divider vertical height={20} margin={isMobile ? 5 : 10} />
              {/* Save Search */}
              <Wrapper mr={1}>
                <SaveSearch searchState={searchState} />
              </Wrapper>
            </>
          )}
          {/* ------- */}
          {/* Settings */}
          <Wrapper
            ml={allFiltersCount > 0 ? 2 : 0}
            display="flex"
            alignItems="center"
          >
            <LoggedInAction
              action={() => setSettingsOpen(true)}
              wrapperStyle={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Tooltip title="Search settings">
                <MdSettings />
              </Tooltip>
            </LoggedInAction>
            <JobSearchSettings
              isOpen={settingsOpen}
              setIsOpen={setSettingsOpen}
              clearSearch={clearSearch}
            />
          </Wrapper>
          {/* ------- */}
          {/* Clear search filters */}
          {allFiltersCount > 0 && !hideClearButton && (
            <Wrapper ml={2} display="flex" alignItems="center">
              <Tooltip title="Clear all filters">
                <MdClear onClick={clearSearchState} />
              </Tooltip>
              <JobSearchSettings
                isOpen={settingsOpen}
                setIsOpen={setSettingsOpen}
              />
            </Wrapper>
          )}
        </Wrapper>
      )}
    </SearchPageListHeaderWrapper>
  )
}
